import React, { useContext, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Formik } from 'formik'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import useQueryLibrary from 'hooks/useQueryLibrary'
import FetchContext from '../../context/fetch/FetchContext'
import { rootDataResponseHandler } from '../../helpers/responseHandlers'
import Page from '../../components/pages/Page'
import Section from '../../components/containers/Section'
import AppFormContainer from 'components/forms/AppFormContainer'
import AppFormSection from 'components/forms/AppFormSection'
import emptyChoicesToNull from 'components/forms/emptyChoicesToNull'
import useFieldStore from 'components/forms/useFieldStore'
import { applyServerErrors } from 'components/forms/serverErrors'
import ContactDetailsSection from '../../components/contacts/ContactDetailsSection'
import ContactAddressesSection from '../../components/contacts/ContactAddressesSection'
import LockedMark from '../../components/contacts/LockedMark'
import useContactPageValidations from '../../components/contacts/useContactPageValidations'
import useOptimisticList from '../../components/contacts/useOptimisticList'
import { addressFieldLabelOf, detailFieldLabelOf } from '../../components/contacts/contactLabels'
import {
    toAddressBody,
    toDetailBody,
    withFlag,
} from '../../components/contacts/contactLists'
import useMarketingDecision, { withDecision } from '../../components/consent/useMarketingDecision'
import schema from 'schemas/company'

const EMPTY_COMPANY = {
    companyId: '',
    companyName: '',
    companyType: '',
    reportingPeriodType: '',
}

const SECTION_FIELDS = {
    basic: ['companyName', 'companyId'],
    taxes: ['companyType', 'reportingPeriodType'],
}

function pickFields(company, keys) {
    return keys.reduce((picked, key) => {
        picked[key] = company?.[key] ?? EMPTY_COMPANY[key]
        return picked
    }, {})
}

const COMPANY_DETAILS_LENS = {
    get: (cached) => cached?.data?.contact?.details ?? [],
    set: (cached, details) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                details,
            },
        },
    }),
}

const COMPANY_ADDRESSES_LENS = {
    get: (cached) => cached?.data?.contact?.addresses ?? [],
    set: (cached, addresses) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                addresses,
            },
        },
    }),
}

function CompanyContactSections({
    companyId,
    locked,
    detailsList,
    addressesList,
}) {
    const details = detailsList.items
    const addresses = addressesList.items

    const storeDetailFlagChange = async (id, key, wanted) => {
        const detail = detailsList.items.find((item) => item.id === id)
        const flags = withFlag(detail?.flags, key, wanted)
        const outcome = await detailsList.update(id, { flags })

        if (outcome.refused != null) {
            throw outcome.refused
        }
    }

    const applyDecision = (decided) => {
        detailsList.setItems((current) => withDecision(current, decided))    }

    const {
        onFlagChange: handleDetailFlagChange,
        dialog: marketingConsentDialog,
        deciding,
    } = useMarketingDecision({
        details,
        consentPathOf: (detailId) => `/companies/${companyId}/details/${detailId}/consent`,
        onDecided: applyDecision,
        onFlagChange: storeDetailFlagChange,
    })

    const updateAddress = ({ id, ...input }) => addressesList.update(id, input)

    const addDetail = (input) => detailsList.create({
        ...input,
        flags: [],
    })

    const updateDetail = ({ id, ...input }) => detailsList.update(id, input)

    const removeMarketingDetail = (id) => {
        detailsList.remove(id)
        return detailsList.confirmRemoval(id)
    }

    const sectionsBusy = deciding

    return (
        <>
            <Section title="כתובות" xs={12}>
                {locked.addresses && <LockedMark testId="contact-addresses-locked" />}
                <ContactAddressesSection
                    addresses={addresses}
                    onAdd={addressesList.create}
                    onUpdate={updateAddress}
                    onRemove={addressesList.remove}
                    onRestore={addressesList.restore}
                    onConfirmRemoval={addressesList.confirmRemoval}
                    refusals={addressesList.refusals}
                    onReorder={addressesList.reorder}
                    disabled={locked.addresses}
                    busy={sectionsBusy}
                />
            </Section>
            <Section title="פרטי קשר" xs={12}>
                {locked.details && <LockedMark testId="contact-details-locked" />}
                <ContactDetailsSection
                    details={details}
                    onAdd={addDetail}
                    onUpdate={updateDetail}
                    onRemove={detailsList.remove}
                    onRemoveMarketing={removeMarketingDetail}
                    onRestore={detailsList.restore}
                    onConfirmRemoval={detailsList.confirmRemoval}
                    refusals={detailsList.refusals}
                    onReorder={detailsList.reorder}
                    onFlagChange={handleDetailFlagChange}
                    flags={['marketing']}
                    disabled={locked.details}
                    busy={sectionsBusy}
                />
                {marketingConsentDialog}
            </Section>
        </>
    )
}

function CompanyFieldsForm({
    title,
    section,
    source,
    fields,
    store,
    onRefused,
    formikRef,
}) {
    const fieldStore = useFieldStore({
        store,
        onRefused,
    })

    const heldSource = useRef(source)
    if (!fieldStore.held) {
        heldSource.current = source
    }

    const sectionSchema = useMemo(() => schema.pick(SECTION_FIELDS[section]), [section])

    return (
        <Formik
            innerRef={formikRef}
            onSubmit={() => {}}
            enableReinitialize={true}
            validationSchema={sectionSchema}
            initialValues={heldSource.current}
        >
            {(formik) => (
                <AppFormSection
                    title={title}
                    section={section}
                    fields={fields}
                    xs={12}
                    md={6}
                    pendingFields={fieldStore.pending}
                    onFieldFocus={(name) => fieldStore.onFocus(formik, name)}
                    onFieldBlur={(name) => fieldStore.onBlur(formik, name)}
                    onFieldChoose={(name, value) => fieldStore.onChoose(formik, name, value)}
                />
            )}
        </Formik>
    )
}

const CompanyEditForm = () => {
    const queryLibrary = useQueryLibrary()

    const { companyId } = useParams()

    const queryClient = useQueryClient()

    const fetcher = useContext(FetchContext)

    const { fields } = schema.describe()

    const labelOf = (field) => fields[field]?.label

    const basicForm = useRef(null)
    const taxesForm = useRef(null)

    const {
        data: loaded,
        isInitialLoading,
    } = useQuery({
        queryKey: queryLibrary.oneCompanyQuery(companyId),
        queryFn: () => fetcher.authenticatedFetch({
            path: `/companies/${companyId}`,
            responseHandler: rootDataResponseHandler,
        }),
        enabled: fetcher.isAuthenticated(),
    })

    const data = loaded?.data
    const loadValidations = loaded?.meta?.validations

    const { locked, showSaved, showRefusal } = useContactPageValidations(loadValidations, { labelOf })

    const refetchCompany = () => queryClient.invalidateQueries({
        queryKey: queryLibrary.oneCompanyQuery(companyId),
    })

    const contactId = data?.contact?.id ?? null

    const refetchFirstContact = async () => {
        if (contactId == null) {
            await refetchCompany()
        }
    }

    const detailsList = useOptimisticList({
        queryKey: queryLibrary.oneCompanyQuery(companyId),
        lens: COMPANY_DETAILS_LENS,
        path: `/companies/${companyId}/details`,
        positionsPath: `/companies/${companyId}/detail-positions`,
        scope: `company-${companyId}:details`,
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: detailFieldLabelOf }),
        onStored: refetchFirstContact,
    })

    const addressesList = useOptimisticList({
        queryKey: queryLibrary.oneCompanyQuery(companyId),
        lens: COMPANY_ADDRESSES_LENS,
        path: `/companies/${companyId}/addresses`,
        positionsPath: `/companies/${companyId}/address-positions`,
        scope: `company-${companyId}:addresses`,
        toInput: toAddressBody,
        labelOf: addressFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: addressFieldLabelOf }),
        onStored: refetchFirstContact,
    })

    const fieldsMutation = useMutation({
        mutationFn: (changes) => {
            const sent = emptyChoicesToNull(changes, fields)

            return fetcher.authenticatedFetch({
                method: 'patch',
                path: `/companies/${companyId}`,
                data: {
                    data: sent,
                },
            })
        },
        onSuccess: async () => {
            showSaved()
            await refetchCompany()
        },
    })

    const showFieldErrors = (fieldErrors) => {
        const forms = [basicForm, taxesForm]

        forms.forEach((form) => {
            const formik = form.current
            if (formik == null) {
                return
            }

            const owned = Object.entries(fieldErrors).filter(([path]) => path in formik.values)
            if (owned.length === 0) {
                return
            }

            applyServerErrors(formik, Object.fromEntries(owned))
        })
    }

    const handleFieldRefusal = (error) => {
        const fieldErrors = showRefusal(error)
        showFieldErrors(fieldErrors)
    }

    const storeFields = (changes) => fieldsMutation.mutateAsync(changes)

    const commands = [
        {
            scheme: 'link',
            label: 'רשומות',
            to: `entries`,
            position: 'end',
            color: 'info',
        },
        {
            scheme: 'link',
            icon: 'arrow_upward',
            to: `/companies`,
            position: 'end',
            color: 'info',
        },
    ]

    return (
        <Page
            title={data?.companyName ?? 'ללא שם'}
            commands={commands}
            busy={isInitialLoading}
        >
            <AppFormContainer>
                <CompanyFieldsForm
                    title="פרטים בסיסיים"
                    section="basic"
                    source={pickFields(data, SECTION_FIELDS.basic)}
                    fields={fields}
                    store={storeFields}
                    onRefused={handleFieldRefusal}
                    formikRef={basicForm}
                />
                <CompanyFieldsForm
                    title="מיסוי"
                    section="taxes"
                    source={pickFields(data, SECTION_FIELDS.taxes)}
                    fields={fields}
                    store={storeFields}
                    onRefused={handleFieldRefusal}
                    formikRef={taxesForm}
                />
            </AppFormContainer>
            {data != null && (
                <AppFormContainer>
                    <CompanyContactSections
                        companyId={companyId}
                        locked={locked}
                        detailsList={detailsList}
                        addressesList={addressesList}
                    />
                </AppFormContainer>
            )}
        </Page>
    )
}

export default CompanyEditForm
